import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Website/Navbar';
import Footer from '../components/Website/Footer';
import { jobsAPI } from '../services/api';
import './Careers.css';

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  job_type: string;
  experience_required: string;
  description: string;
  requirements: string[];
  is_active: boolean;
  created_at: string;
}

const Careers: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await jobsAPI.getActiveJobs();
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setError('Failed to load job listings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getJobTypeIcon = (type: string) => {
    switch(type) {
      case 'full_time':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="6" width="16" height="12" rx="2"/>
            <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6"/>
          </svg>
        );
      case 'part_time':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6V12L16 14"/>
          </svg>
        );
      case 'contract':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"/>
            <path d="M14 2V8H20"/>
          </svg>
        );
      case 'remote':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7L12 12L22 7L12 2Z"/>
            <path d="M2 17L12 22L22 17"/>
            <path d="M2 12L12 17L22 12"/>
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="6" width="16" height="12" rx="2"/>
            <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6"/>
          </svg>
        );
    }
  };

  const getJobTypeLabel = (type: string) => {
    switch(type) {
      case 'full_time': return 'Full-time';
      case 'part_time': return 'Part-time';
      case 'contract': return 'Contract';
      case 'remote': return 'Remote';
      default: return type;
    }
  };

  const getJobTypeColor = (type: string) => {
    switch(type) {
      case 'full_time': return '#0d47a1';
      case 'part_time': return '#10b981';
      case 'contract': return '#f59e0b';
      case 'remote': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const handleApply = (jobId: number, jobTitle: string) => {
    navigate(`/apply?jobId=${jobId}&job=${encodeURIComponent(jobTitle)}`);
  };

  // SVG Icons
  const Icons = {
    Department: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="6" width="16" height="12" rx="2"/>
        <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6"/>
      </svg>
    ),
    Location: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
    Experience: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    Check: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17L4 12"/>
      </svg>
    ),
    ArrowRight: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    ),
    Spinner: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
      </svg>
    ),
    Empty: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    Error: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <circle cx="12" cy="16" r="0.5" fill="currentColor"/>
      </svg>
    ),
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="careers-loading">
          <div className="careers-spinner">
            <Icons.Spinner />
          </div>
          <p>Loading job opportunities...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="careers-error">
          <div className="careers-error-icon">
            <Icons.Error />
          </div>
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="careers-page">
        <div className="careers-container">
          <div className="careers-header">
            <div className="careers-header-badge">Join Our Team</div>
            <h1 className="careers-title">Build Your Career</h1>
            <p className="careers-subtitle">
              Build your career with us and help shape the future of technology
            </p>
          </div>

          {jobs.length === 0 ? (
            <div className="careers-empty">
              <div className="careers-empty-icon">
                <Icons.Empty />
              </div>
              <h3>No Open Positions</h3>
              <p>Currently there are no open positions. Please check back later!</p>
            </div>
          ) : (
            <>
              <div className="careers-stats">
                <span className="careers-stats-count">{jobs.length} open position{jobs.length > 1 ? 's' : ''}</span>
              </div>
              <div className="careers-grid">
                {jobs.map(job => (
                  <div key={job.id} className="careers-job-card">
                    <div className="careers-job-header">
                      <h2 className="careers-job-title">{job.title}</h2>
                      <span 
                        className="careers-job-type"
                        style={{ 
                          color: getJobTypeColor(job.job_type),
                          background: `${getJobTypeColor(job.job_type)}10`
                        }}
                      >
                        {getJobTypeIcon(job.job_type)}
                        {getJobTypeLabel(job.job_type)}
                      </span>
                    </div>
                    <div className="careers-job-details">
                      <div className="careers-job-detail">
                        <Icons.Department />
                        <span>{job.department}</span>
                      </div>
                      <div className="careers-job-detail">
                        <Icons.Location />
                        <span>{job.location}</span>
                      </div>
                      <div className="careers-job-detail">
                        <Icons.Experience />
                        <span>{job.experience_required}</span>
                      </div>
                    </div>
                    <p className="careers-job-description">{job.description}</p>
                    <ul className="careers-job-requirements">
                      {job.requirements.map((req, idx) => (
                        <li key={idx}>
                          <Icons.Check />
                          {req}
                        </li>
                      ))}
                    </ul>
                    <button 
                      className="careers-apply-btn" 
                      onClick={() => handleApply(job.id, job.title)}
                    >
                      Apply Now
                      <Icons.ArrowRight />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Careers;